import {
  BarChart3,
  BookOpen,
  CalendarDays,
  CreditCard,
  DollarSign,
  FileText,
  Hammer,
  Home,
  Landmark,
  Layers,
  ListChecks,
  Map as MapIcon,
  Megaphone,
  MessageCircle,
  Package,
  ReceiptText,
  ScrollText,
  Settings,
  ShieldCheck,
  Store,
  Tags,
  Truck,
  UserCog,
  Wallet,
  Plus,
  Users,
  type LucideIcon,
} from 'lucide-react';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Shown when the user has any of these permissions. */
  anyOf?: string[];
  /** Needs the permission without an assignee restriction (the screen spans every record). */
  unrestricted?: boolean;
  /** Matches nested routes as active. */
  prefix?: boolean;
  /** One sentence saying what the screen is for (tooltip in the menu, line in the help). */
  hint?: string;
}

export interface NavSection {
  title?: string;
  items: NavItem[];
}

export const NAV: NavSection[] = [
  { items: [{ to: '/inicio', label: 'Inicio', icon: Home, hint: 'Lo que tenés que hacer hoy y cómo viene todo.' }] },
  {
    title: 'Vender',
    items: [
      { to: '/consultas', label: 'Consultas', icon: MessageCircle, anyOf: ['inquiries.read'], prefix: true, hint: 'Gente que pregunta por un trabajo. De acá sale el presupuesto.' },
      { to: '/presupuestos', label: 'Presupuestos', icon: FileText, anyOf: ['quotes.read'], prefix: true, hint: 'Armá el precio de un trabajo y mandáselo al cliente.' },
      { to: '/clientes', label: 'Clientes y obras', icon: Users, anyOf: ['clients.read'], prefix: true, hint: 'Quiénes nos compran y dónde van los trabajos.' },
    ],
  },
  {
    title: 'Fabricar y entregar',
    items: [
      { to: '/trabajos', label: 'Trabajos', icon: Hammer, anyOf: ['works.read'], prefix: true, hint: 'Presupuestos aceptados: medir, cortar, entregar y cobrar.' },
      { to: '/agenda', label: 'Agenda y rutas', icon: CalendarDays, anyOf: ['works.read'], hint: 'Qué visitas hay cada día y el mejor camino para hacerlas.' },
      { to: '/mapa', label: 'Mapa de obras', icon: MapIcon, anyOf: ['works.read'], hint: 'Dónde están los trabajos que tenemos en marcha.' },
      { to: '/stock', label: 'Stock de placas', icon: Layers, anyOf: ['inventory.read'], prefix: true, hint: 'Las placas y los sobrantes que hay en el taller.' },
      { to: '/productos', label: 'Productos', icon: Package, anyOf: ['products.read'], prefix: true, hint: 'Cosas chicas que hacemos con sobrantes para vender (tablas, posavasos).' },
    ],
  },
  {
    title: 'Comprar',
    items: [
      { to: '/proveedores', label: 'Proveedores', icon: Truck, anyOf: ['suppliers.read'], prefix: true, hint: 'A quiénes les compramos y cuánto les debemos.' },
      { to: '/comprobantes', label: 'Facturas de proveedores', icon: ReceiptText, anyOf: ['source-docs.read'], prefix: true, hint: 'Subí las facturas y remitos que nos mandan para anotar lo que compramos.' },
      { to: '/precios', label: 'Precios y alertas', icon: Tags, anyOf: ['costs.read'], hint: 'Cuánto nos cobra cada proveedor y avisos cuando algo sube.' },
    ],
  },
  {
    title: 'Ventas y marketing',
    items: [
      { to: '/mercado-libre', label: 'Mercado Libre', icon: Store, anyOf: ['marketplace.read'], prefix: true, hint: 'Lo que vendemos por Mercado Libre.' },
      { to: '/marketing', label: 'Marketing', icon: Megaphone, anyOf: ['marketing.read'], prefix: true, hint: 'Publicidades y cuántos clientes nos trajeron.' },
    ],
  },
  {
    title: 'Plata',
    items: [
      { to: '/caja', label: 'Caja', icon: Wallet, anyOf: ['payments.read'], unrestricted: true, hint: 'Toda la plata que entró y salió.' },
      { to: '/cuentas', label: 'Nuestras cuentas', icon: CreditCard, anyOf: ['payments.read'], unrestricted: true, hint: 'Bancos, Mercado Pago y efectivo donde guardamos la plata.' },
      { to: '/tableros', label: 'Gráficos y números', icon: BarChart3, hint: 'Cómo viene el negocio, en gráficos.', anyOf: ['payments.read', 'margins.read', 'products.read', 'marketing.read'] },
    ],
  },
  {
    title: 'Ajustes',
    items: [
      { to: '/catalogo', label: 'Lista de precios', icon: BookOpen, anyOf: ['catalog.read'], hint: 'Lo que vendemos y a cuánto. El presupuesto toma los precios de acá.' },
      { to: '/cotizacion', label: 'Precio del dólar', icon: DollarSign, anyOf: ['fx.read'], hint: 'El dólar que usamos para pasar precios a pesos.' },
      { to: '/titulares', label: 'A nombre de quién', icon: Landmark, hint: 'Las personas o empresas nuestras que firman los presupuestos y cobran.', anyOf: ['suppliers.read', 'clients.read', 'quotes.write'] },
      { to: '/sistema/usuarios', label: 'Usuarios', icon: UserCog, anyOf: ['users.manage'], hint: 'Quiénes pueden entrar al sistema.' },
      { to: '/sistema/roles', label: 'Roles y permisos', icon: ShieldCheck, anyOf: ['users.manage'], hint: 'Qué puede hacer cada persona.' },
      { to: '/configuracion', label: 'Configuración', icon: Settings, anyOf: ['settings.read'], prefix: true, hint: 'Datos del negocio y conexión con otros servicios.' },
      { to: '/sistema/auditoria', label: 'Historial de cambios', icon: ScrollText, anyOf: ['audit.read'], hint: 'Quién cambió qué y cuándo.' },
      { to: '/sistema/tareas', label: 'Tareas automáticas', icon: ListChecks, anyOf: ['tasks.manage'], hint: 'Lo que el sistema hace solo, y si algo falló.' },
    ],
  },
];

export const QUICK_ACTIONS = [{ to: '/presupuestos/nuevo', label: 'Nuevo presupuesto', icon: Plus, anyOf: ['quotes.write'] }];
